/* ==========================================================================
   TrendyBits · made-to-order quick-add guard
   --------------------------------------------------------------------------
   THE HOLE THIS CLOSES
   A product tagged "custom-text" needs a name typed into the personalisation
   field before it can be printed. That field only exists on the product page.
   Every grid in the theme, though, offers a one-click add straight from the
   card, so a shopper could put a made-to-order product in the cart with no
   text at all and the order would arrive unfulfillable.

   There are four such controls, all of them covered here:
     · .tb-coll-card__quick-add   sections/main-tb-collection.liquid (form POST)
     · .quick-add__submit         snippets/card-product.liquid (Dawn product-form)
     · .quick-add__submit[data-product-url]  the multi-variant quick-add modal,
       which injects PDP markup that the personalisation script never wires up
     · [data-tb-quick-add]        sections/tb-pdp-related.liquid (own fetch)

   HOW IT DECIDES
   None of those cards expose product tags in their markup — the only thing
   readable from the DOM is the product handle in the card's link. So the first
   time a shopper quick-adds a given product the click is held, /products/
   <handle>.js is fetched (a few hundred bytes) and its tags are read. The
   answer is cached in sessionStorage, so it costs one small request per
   product per session and nothing at all after that.

   That means there is no list to maintain: tag a new product "custom-text"
   in admin and it is guarded immediately, with no theme edit.

   WHAT HAPPENS ON A HIT
   The add is cancelled and the shopper goes to the product page with
   #tb-personalise, which scrolls the personalisation card into view, focuses
   the first field and flashes it. They are not blocked, they are routed to
   the one place where they can finish the job.

   FAILURE MODE IS DELIBERATE
   If the tag lookup fails for any reason — offline, 404, malformed JSON — the
   original click is replayed and the add goes through. A guard that silently
   breaks add-to-cart on the whole store would be far worse than the hole it
   was closing.

   The PDP's own form is never touched: assets/tb-personalise.js already locks
   that one, and this file bails out on anything inside #tb-pdp-form.

   To disable: delete the tb-guard-quickadd.js script line from
   snippets/tb-made-to-order.liquid.
   ========================================================================== */

(function () {
  'use strict';

  var TAG = 'custom-text';
  var STORE_KEY = 'tb-mto-v1';
  var HASH = '#tb-personalise';

  var CLICK_SEL = [
    '.tb-coll-card__quick-add',
    '.quick-add__submit',
    '[data-tb-quick-add]',
    '.quick-add button[type="submit"]'
  ].join(',');

  var cache = {};
  try {
    var raw = sessionStorage.getItem(STORE_KEY);
    if (raw) cache = JSON.parse(raw) || {};
  } catch (e) {
    cache = {};
  }

  function remember(handle, isCustom) {
    cache[handle] = isCustom ? 1 : 0;
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(cache)); } catch (e) {}
  }

  /* ---------- working out which product a control belongs to ---------- */

  function handleFromHref(href) {
    var m = String(href || '').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  /* Walks up from the control looking for the card's own product link. Eight
     hops clears the deepest card in the theme (button > .quick-add >
     .card__media > .card__inner > .card > .card-wrapper) with room spare. */
  function targetOf(el) {
    var owner = el.closest ? el.closest('[data-product-url]') : null;
    if (owner) {
      var raw = owner.getAttribute('data-product-url');
      if (handleFromHref(raw)) return { handle: handleFromHref(raw), url: raw };
    }

    var node = el;
    var hops = 0;
    while (node && hops < 8) {
      var name = node.nodeName;
      if (name === 'BODY' || name === 'MAIN' || name === 'HTML') break;
      var link = node.querySelector ? node.querySelector('a[href*="/products/"]') : null;
      if (link) {
        var h = handleFromHref(link.getAttribute('href'));
        if (h) return { handle: h, url: link.getAttribute('href') };
      }
      node = node.parentElement;
      hops++;
    }
    return null;
  }

  function isCustomProduct(json) {
    var tags = (json && json.tags) || [];
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i]).trim().toLowerCase() === TAG) return true;
    }
    return false;
  }

  /* ---------- acting on the decision ---------- */

  function sendToProduct(url) {
    var clean = String(url).split('#')[0];
    window.location.href = clean + HASH;
  }

  function busy(el, on) {
    if (!el || !el.style) return;
    el.style.opacity = on ? '0.55' : '';
    el.style.pointerEvents = on ? 'none' : '';
    if (on) { el.setAttribute('aria-busy', 'true'); }
    else { el.removeAttribute('aria-busy'); }
  }

  function replayClick(el) {
    el.__tbGuardOk = true;
    try { el.click(); } catch (e) {}
    setTimeout(function () { el.__tbGuardOk = false; }, 0);
  }

  function replaySubmit(form) {
    form.__tbGuardOk = true;
    try {
      if (form.requestSubmit) form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    } catch (e) {}
    setTimeout(function () { form.__tbGuardOk = false; }, 0);
  }

  function stop(ev) {
    ev.preventDefault();
    ev.stopPropagation();
    if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
  }

  /* control  = the thing to re-fire if the product turns out to be ordinary
     spinner  = the thing to grey out while we look the product up          */
  function guard(ev, control, spinner, replay) {
    if (control.__tbGuardOk) return;
    if (control.closest && control.closest('#tb-pdp-form')) return;

    var target = targetOf(control);
    if (!target) return;

    var known = cache[target.handle];
    if (known === 0) return;
    if (known === 1) { stop(ev); sendToProduct(target.url); return; }

    stop(ev);
    busy(spinner, true);

    fetch('/products/' + encodeURIComponent(target.handle) + '.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) {
        if (!r.ok) throw new Error('lookup failed');
        return r.json();
      })
      .then(function (json) {
        var custom = isCustomProduct(json);
        remember(target.handle, custom);
        if (custom) { sendToProduct(target.url); return; }
        busy(spinner, false);
        replay(control);
      })
      .catch(function () {
        /* Never leave a shopper unable to buy because a lookup failed. */
        busy(spinner, false);
        replay(control);
      });
  }

  document.addEventListener('click', function (ev) {
    var el = ev.target && ev.target.closest ? ev.target.closest(CLICK_SEL) : null;
    if (!el) return;
    if (el.tagName === 'A' && !el.matches('[data-tb-quick-add]')) return;
    guard(ev, el, el, replayClick);
  }, true);

  /* Keyboard submits and anything that posts a cart form without a click. */
  document.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (!form || form.nodeName !== 'FORM') return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    if (form.id === 'tb-pdp-form') return;
    if (form.closest('#tb-pdp-form, .tb-pdp__buy')) return;
    guard(ev, form, form.querySelector('[type="submit"]'), replaySubmit);
  }, true);

  /* ---------- arriving on the product page from a blocked quick-add ---------- */

  /* Centres the card in the viewport. Deliberately an instant scrollTo rather
     than scrollIntoView with smooth behaviour: a smooth scroll started while
     the page is still loading gets cancelled by the first image that lands,
     which is exactly what happened in testing. */
  function place(card) {
    var box = card.getBoundingClientRect();
    var to = box.top + window.pageYOffset - (window.innerHeight / 2) + (box.height / 2);
    window.scrollTo(0, Math.max(0, Math.round(to)));
  }

  if (window.location.hash === HASH) {
    var tries = 0;
    var find = setInterval(function () {
      var card = document.querySelector('[data-tbpz-card]');
      if (!card && ++tries < 40) return;
      clearInterval(find);
      if (!card) return;

      place(card);
      /* Late images still shove the page around, so land it a second time. */
      setTimeout(function () { place(card); }, 600);
      setTimeout(function () {
        card.classList.add('tbpz-flash');
        var input = card.querySelector('.tbpz__in');
        if (input && !input.disabled) input.focus({ preventScroll: true });
      }, 700);
      setTimeout(function () { card.classList.remove('tbpz-flash'); }, 3600);
    }, 100);
  }
})();